import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';

import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import Percentage from '@/main/components/shared/percentage/percentage';
import KOGRadio from '@/main/components/shared/radioButton/radioButton';
import SelectXnuupPartIdAutoCompleteComponent from '@/main/components/shared/xnuupPartIdAutoComplete/selectXnuupPartIdAutoCompleteComponent';

import { KeyValue } from '@/main/models/common';
import { PositionRequest } from '@/main/models/request';
import { SubcategoryRequest } from '@/main/models/request/subcategoryRequest';
import { QuoteItemMappingPartViewReponse, XnuupPartIdAutoCompleteResponse } from '@/main/models/response';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { getCategory, useCategoryReducer } from '@/main/store/category';
import { getPosition, resetReducerPosition, usePositionReducer } from '@/main/store/position';
import { getSubcategory, resetReducerSubcategory, useSubcategoryReducer } from '@/main/store/subcategory';

import { IFormDetailsMappingParts } from './detailsMappingParts';
import * as S from './styles';

interface IRowManualMappingPartsProps {
  optionSelected: string;
  mappingParts: QuoteItemMappingPartViewReponse
  setOptionSelected: React.Dispatch<React.SetStateAction<string>>
}
interface CategoryDropdown extends KeyValue {
  ignoreWatcher?: boolean;
}
function RowManualMappingParts({
  optionSelected,
  mappingParts,
  setOptionSelected
}: IRowManualMappingPartsProps) {
  const KEY_REDUCER = `RowManualMappingParts_${mappingParts.guidQuoteItem}`
  const dispatch = useDispatch();
  const {
    control,
    formState: { errors },
    clearErrors,
    setValue,
    resetField,
    register
  } = useFormContext();
  const watcherCategory = useWatch({
    control: control,
    name: 'category',
  });
  const watcherSubCategory = useWatch({
    control: control,
    name: 'subCategory',
  });
  const watcherPart = useWatch({
    control: control,
    name: 'part',
  });
  const { category: categoryReducer } = useCategoryReducer();
  const { isolated: subcategoryReducer } = useSubcategoryReducer(KEY_REDUCER);
  const { isolated: positionReducer } = usePositionReducer(KEY_REDUCER);

  const [hasClearValue, setHasClearValue] = useState(false);

  //#region Core Functions
  function resetReducers(keyFields: (keyof IFormDetailsMappingParts)[] = []) {
    const mapReducerField: { [K in keyof IFormDetailsMappingParts]?: (keyReducerAction?: string) => AnyAction } = {
      subCategory: resetReducerSubcategory,
      position: resetReducerPosition,
      part: resetReducerPosition,
    };
    const keysAllFields = Object.keys(mapReducerField) as (keyof IFormDetailsMappingParts)[];
    const fieldsToReset = keyFields.length === 0 ? keysAllFields : keyFields;

    Object.entries(mapReducerField)
      .forEach(([key, resetReducerField]) => {
        if (fieldsToReset.includes(key as keyof IFormDetailsMappingParts)) {
          dispatch(resetReducerField(KEY_REDUCER))
        }
      })

  }
  function resetFields(keyFields: (keyof IFormDetailsMappingParts)[] | keyof IFormDetailsMappingParts = []) {
    const arrayFields = Array.isArray(keyFields) ? keyFields : [keyFields];
    arrayFields.forEach((key) => {
      if (key === 'partName') setHasClearValue(true);
      resetField(key)
    })
    resetReducers(arrayFields)
  }
  //#endregion

  //#region Call API Domains
  function callCategoryApiDomain() {
    resetFields(['category', 'subCategory', 'position', 'part'])
    if (isNullUndefined(categoryReducer.data)) dispatch(getCategory(false));
  }

  function callSubCategoryApiDomain(categorySelected: CategoryDropdown | null) {
    if (isNullUndefined(categorySelected?.ignoreWatcher)) {
      resetFields(['subCategory', 'part', 'position', 'partName'])
    }

    if (isNullUndefined(categorySelected)) return;
    const request: SubcategoryRequest = {
      guid: categorySelected.key as string,
      isCanastaBasica: false
    }
    dispatch(getSubcategory(request, KEY_REDUCER));
  }
  function loadOptionsByXnuupPartId(partId: XnuupPartIdAutoCompleteResponse) {
    if (!partId) return resetField('part')
    setValue('partName', partId.partPIESName);

    const {
      partCategoryGuid,
      partCategoryName,
      partGuid,
      partPIESName,
      partSubCategoryGuid,
      partSubCategoryName,
      piesPartTerminologyId
    } = partId;

    if (partCategoryGuid && partCategoryName) {
      clearErrors();
      setValue('category', {
        key: partCategoryGuid,
        value: partCategoryName,
        ignoreWatcher: true
      });
      if (isNullUndefined(categoryReducer.data)) dispatch(getCategory(false));

      if (partSubCategoryGuid && partSubCategoryName) {

        setValue('subCategory', {
          key: partSubCategoryGuid,
          value: partSubCategoryName
        });
        const request: SubcategoryRequest = {
          guid: String(partCategoryGuid),
          isCanastaBasica: false
        };
        dispatch(getSubcategory(request, KEY_REDUCER));

        if (partGuid) {
          const request: PositionRequest = {
            partGuid: String(partGuid),
            ShowIdPIES: true
          };
          dispatch(getPosition(request, KEY_REDUCER));
        } else {
          dispatch(resetReducerPosition(KEY_REDUCER));
          setValue('position', null);
        }
      } else {
        dispatch(resetReducerSubcategory(KEY_REDUCER));
        setValue('subCategory', null);
      }
    } else {
      setValue('category', null);
    }

    setValue('part', {
      id: piesPartTerminologyId,
      key: partGuid,
      value: partPIESName
    } as KeyValue);
  }
  //#endregion

  useEffect(() => {
    callCategoryApiDomain();
    return () => {
      resetReducers()
    }
  }, [])

  useEffect(() => {
    callSubCategoryApiDomain(watcherCategory);
  }, [watcherCategory?.key])
  useEffect(() => {
    clearErrors(['category', 'subCategory', 'part', 'position'])
  }, [optionSelected])


  const percentage = 0;
  const disabledCategoryReducer = isNullUndefined(categoryReducer.data);
  const disabledSubCategoryReducer = isNullUndefined(subcategoryReducer.data);
  const disabledPositionReducer = isNullUndefined(positionReducer.data);
  const isChecked = optionSelected === 'manual';

  return (
    <S.ContainerRow $notClickable>
      <KOGRadio
        id={`id-mappingParts-radio-manual`}
        value={'manual'}
        checked={isChecked}
        onChange={(_, checked) => setOptionSelected(checked ? 'manual' : '')}
      />
      <KogAutoCompleteControlled
        id='id-mappingParts-select-category'
        nameCustom='category'
        label={getTranslateByKey('quoteCard.category')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={categoryReducer.data}
        isLoading={categoryReducer.isLoading}
        disabled={disabledCategoryReducer}
        rulesCustom={{ required: isChecked }}
        onChangeCustom={(optionSelected) => { }}
      />
      <KogAutoCompleteControlled
        id='id-mappingParts-select-subCategory'
        nameCustom='subCategory'
        label={getTranslateByKey('quoteCard.subCategory')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={subcategoryReducer.data}
        isLoading={subcategoryReducer.isLoading}
        disabled={disabledSubCategoryReducer}
        rulesCustom={{ required: isChecked }}
        onChangeCustom={() => { }}
      />
      <SelectXnuupPartIdAutoCompleteComponent
        isCanastaBasica={false}
        showIdPIES={true}
        required={isChecked}
        partPIESName={watcherPart?.value}
        watcherCategory={watcherCategory}
        watcherSubcategory={watcherSubCategory}
        handleChangeXnuupPartId={(partId) => {
          loadOptionsByXnuupPartId(partId);
        }}
        errorMessageCustom={
          errors.part?.type === 'required'
            ? getTranslateByKey('information.xnuupPartIdRequired', true)
            : ''
        }
        hasClearValue={hasClearValue}
        setHasClearValue={setHasClearValue}
        hasErrorCustom={!!errors.part}
        label={getTranslateByKey('information.part')}
        {...register('part', { required: isChecked })}
      />
      <KogAutoCompleteControlled
        id='id-mappingParts-select-position'
        nameCustom='position'
        label={getTranslateByKey('information.position')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={positionReducer.data}
        isLoading={positionReducer.isLoading}
        disabled={disabledPositionReducer}
        rulesCustom={{ required: isChecked }}
        onChangeCustom={() => { }}
      />
      <S.ContainerLabel $labelContent={getTranslateByKey('information.reliabilityPercentage')}><Percentage percentage={percentage} /></S.ContainerLabel>
    </S.ContainerRow>
  )
}

export default RowManualMappingParts